/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createBulkEntityRelationships,
  createEntityRelationship,
  deleteEntityRelationship,
  getEntityRelationships,
} from './entityRelationships.service';
import {
  IBulkEntityRelationshipsPayload,
  ICreateEntityRelationship,
  IEntityRelationshipWithNames,
} from './entityRelationships.d';
import { auditKeys } from '../audit/audit.hooks';
import { entityKeys, invalidateEntityLists } from '../entities/entities.keys';

const QUERY_KEYS = {
  ENTITY_RELATIONSHIP: 'entity-relationship',
} as const;

const entityRelationshipKeys = {
  all: () => [QUERY_KEYS.ENTITY_RELATIONSHIP] as const,
  byEntity: (entityId?: string, includeInactive?: boolean) =>
    [
      ...entityRelationshipKeys.all(),
      'by-entity',
      entityId || '',
      includeInactive ? 'all' : 'active',
    ] as const,
};

export const useCreateEntityRelationship = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      payload,
      teamId,
    }: {
      payload: ICreateEntityRelationship;
      teamId?: string;
    }) => createEntityRelationship(payload, teamId),
    onSuccess: (_data, variables) => {
      if (variables?.payload?.source_entity_id) {
        queryClient.invalidateQueries({
          queryKey: auditKeys.auditLogsByEntity(
            variables.payload.source_entity_id,
          ),
          exact: false,
        });
      }
      queryClient.invalidateQueries({
        queryKey: entityRelationshipKeys.all(),
        exact: false,
      });
      // Adding a relationship changes the hierarchy tree:
      // - the child entity is no longer top-level, so entities-by-database must refresh
      // - any expanded parent's children list must refresh
      queryClient.invalidateQueries({
        queryKey: entityKeys.entityChildrenAll(),
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: entityKeys.all(),
        exact: false,
      });
    },
  });
};

export const useDeleteEntityRelationship = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (relationshipId: string) => {
      const response = await deleteEntityRelationship(relationshipId);
      if (!response.success) {
        throw new Error(response.message || 'Failed to remove relationship');
      }
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: entityRelationshipKeys.all(),
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: entityKeys.entityChildrenAll(),
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: entityKeys.all(),
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: auditKeys.all(),
        exact: false,
      });
    },
  });
};

export const useGetEntityRelationships = (
  entityId?: string,
  includeInactive = false,
): {
  data: IEntityRelationshipWithNames[];
  isLoading: boolean;
  error: any;
  refetch: () => void;
} => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: entityRelationshipKeys.byEntity(entityId, includeInactive),
    queryFn: () => getEntityRelationships(entityId, includeInactive),
    enabled: Boolean(entityId),
  });

  return {
    data: data || [],
    isLoading: isLoading,
    error: error,
    refetch: refetch,
  };
};

export const useCreateBulkEntityRelationships = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      payload,
      teamId,
    }: {
      payload: IBulkEntityRelationshipsPayload;
      teamId?: string;
    }) => createBulkEntityRelationships(payload, teamId),
    onSuccess: (data, variables) => {
      if (variables?.payload?.database_id) {
        queryClient.invalidateQueries({
          queryKey: auditKeys.auditLogsByDatabase(
            variables.payload.database_id,
          ),
          exact: false,
        });
      }

      // Invalidate audit logs for each entity involved in the bulk operation
      const entityIds = new Set<string>();
      data?.data?.inserted?.forEach((rel) => {
        if (rel.source_entity_id) entityIds.add(rel.source_entity_id);
        if (rel.target_entity_id) entityIds.add(rel.target_entity_id);
      });
      entityIds.forEach((entityId) => {
        queryClient.invalidateQueries({
          queryKey: auditKeys.auditLogsByEntity(entityId),
          exact: false,
        });
      });

      queryClient.invalidateQueries({
        queryKey: entityRelationshipKeys.all(),
        exact: false,
      });
      invalidateEntityLists(queryClient);
      queryClient.invalidateQueries({
        queryKey: entityKeys.entityChildrenAll(),
        exact: false,
      });
    },
  });
};
